import { utf16Length, IsoTimestampSchema } from "./string-validation"
import { z } from "zod"

import {
	CheckoutOfferingIdSchema,
	ProductIdSchema,
	ProductVariantIdSchema,
	ProductVariantSkuIdSchema,
	VisitFeeRuleIdSchema,
} from "./common"
import {
	RecurringMembershipFieldsInputSchema,
	RecurringMembershipFieldsSchema,
	withRecurringMembershipInputValidation,
	withRecurringMembershipValidation,
} from "./membership"
import { JourneyDefinitionIdSchema } from "./journey-definitions"

export const SHORT_TEXT_MAX_LENGTH = 255
export const LONG_TEXT_MAX_LENGTH = 10_000

const ShortTextInputSchema = z.string().trim().apply(utf16Length("max", SHORT_TEXT_MAX_LENGTH))
const RequiredShortTextInputSchema = ShortTextInputSchema.min(1)
const ProductDescriptionInputSchema = z.string().trim().apply(utf16Length("max", LONG_TEXT_MAX_LENGTH))

export const VisitTypeSchema = z
	.enum(["synchronous", "asynchronous", "none"])
	.meta({ id: "VisitType" })

export type VisitType = z.infer<typeof VisitTypeSchema>

export const ProductVariantSkuSchema = z
	.object({
		id: ProductVariantSkuIdSchema,
		productVariantId: ProductVariantIdSchema,
		code: z.string().min(1).nullable(),
		name: z.string().min(1).nullable(),
		description: z.string().nullable(),
		daysSupply: z.number().int().positive(),
		currency: z.string().min(1),
		unitAmountCents: z.number().int().nonnegative(),
		isActive: z.boolean(),
		createdAt: IsoTimestampSchema.optional(),
		updatedAt: IsoTimestampSchema.optional(),
	})
	.meta({ id: "ProductVariantSku" })

export type ProductVariantSku = z.infer<typeof ProductVariantSkuSchema>

export const ProductSchema = z
	.object({
		id: ProductIdSchema,
		ndc: z.string().min(1),
		sku: z.string().nullable(),
		productCode: z.string().nullable(),
		csn: z.string().nullable(),
		name: z.string().min(1),
		description: z.string().nullable(),
		dose: z.string().nullable(),
		daysSupply: z.number().int().positive().nullable(),
		unitOfMeasure: z.string().nullable(),
		form: z.string().nullable(),
		strength: z.string().nullable(),
		isActive: z.boolean(),
		metadata: z.record(z.string(), z.unknown()),
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
	})
	.meta({ id: "Product" })

export type Product = z.infer<typeof ProductSchema>

export const ProductAvailabilityReasonSchema = z
	.enum(["inactive", "missing_active_offering", "missing_availability_assignment"])
	.meta({ id: "ProductAvailabilityReason" })

export type ProductAvailabilityReason = z.infer<typeof ProductAvailabilityReasonSchema>

export const ProductAvailabilitySchema = z
	.object({
		hasActiveOffering: z.boolean(),
		hasAvailabilityAssignment: z.boolean(),
		checkoutAvailable: z.boolean(),
		reasons: z.array(ProductAvailabilityReasonSchema).default([]),
	})
	.meta({ id: "ProductAvailability" })

export type ProductAvailability = z.infer<typeof ProductAvailabilitySchema>

export const ProductVariantSchema = z
	.object({
		id: ProductVariantIdSchema,
		productId: ProductIdSchema,
		code: z.string().min(1).nullable(),
		name: z.string().min(1).nullable(),
		description: z.string().nullable(),
		dose: z.string().nullable(),
		isActive: z.boolean(),
		createdAt: IsoTimestampSchema.optional(),
		updatedAt: IsoTimestampSchema.optional(),
		skus: z.array(ProductVariantSkuSchema).default([]),
	})
	.meta({ id: "ProductVariant" })

export type ProductVariant = z.infer<typeof ProductVariantSchema>

export const CheckoutOfferingAssignmentSchema = z
	.object({
		id: z.uuid(),
		checkoutOfferingId: CheckoutOfferingIdSchema,
		journeyDefinitionId: JourneyDefinitionIdSchema,
		journeyDefinitionVersion: z.number().int().positive(),
		visitType: VisitTypeSchema,
		isActive: z.boolean(),
		createdAt: IsoTimestampSchema.optional(),
		updatedAt: IsoTimestampSchema.optional(),
	})
	.meta({ id: "CheckoutOfferingAssignment" })

export type CheckoutOfferingAssignment = z.infer<typeof CheckoutOfferingAssignmentSchema>

export const CheckoutOfferingSchema = withRecurringMembershipValidation(
	z.object({
		id: CheckoutOfferingIdSchema,
		productId: ProductIdSchema,
		code: z.string().min(1).nullable(),
		name: z.string().min(1),
		description: z.string().nullable(),
		daysSupply: z.number().int().positive(),
		currency: z.string().min(1),
		unitAmountCents: z.number().int().nonnegative(),
		isActive: z.boolean(),
		createdAt: IsoTimestampSchema.optional(),
		updatedAt: IsoTimestampSchema.optional(),
		assignments: z.array(CheckoutOfferingAssignmentSchema).default([]),
	})
	.extend(RecurringMembershipFieldsSchema.shape),
)
	.meta({ id: "CheckoutOffering" })

export type CheckoutOffering = z.infer<typeof CheckoutOfferingSchema>

export const JourneyDefinitionOfferingAssignmentInputSchema = z
	.object({
		journeyDefinitionId: JourneyDefinitionIdSchema,
		journeyDefinitionVersion: z.number().int().positive(),
		visitType: VisitTypeSchema,
		isActive: z.boolean().optional(),
	})
	.meta({ id: "JourneyDefinitionOfferingAssignmentInput" })

export type JourneyDefinitionOfferingAssignmentInput = z.infer<
	typeof JourneyDefinitionOfferingAssignmentInputSchema
>

export const VisitFeeRuleSchema = z
	.object({
		id: VisitFeeRuleIdSchema,
		code: z.string().min(1).nullable(),
		name: z.string().min(1),
		description: z.string().nullable(),
		visitType: VisitTypeSchema,
		journeyDefinitionId: JourneyDefinitionIdSchema.nullable(),
		journeyDefinitionVersion: z.number().int().positive().nullable(),
		currency: z.string().min(1),
		unitAmountCents: z.number().int().nonnegative(),
		isActive: z.boolean(),
		createdAt: IsoTimestampSchema.optional(),
		updatedAt: IsoTimestampSchema.optional(),
	})
	.superRefine((rule, context) => {
		if ((rule.journeyDefinitionId === null) !== (rule.journeyDefinitionVersion === null)) {
			context.addIssue({
				code: "custom",
				message: "Journey definition ID and version must both be set or both be null.",
				path: ["journeyDefinitionVersion"],
			})
		}
	})
	.meta({ id: "VisitFeeRule" })

export type VisitFeeRule = z.infer<typeof VisitFeeRuleSchema>

export const CreateProductSchema = z
	.object({
		ndc: RequiredShortTextInputSchema,
		sku: RequiredShortTextInputSchema.optional(),
		productCode: RequiredShortTextInputSchema.optional(),
		csn: RequiredShortTextInputSchema.optional(),
		name: RequiredShortTextInputSchema,
		description: ProductDescriptionInputSchema.optional(),
		dose: ShortTextInputSchema.optional(),
		daysSupply: z.number().int().positive().optional(),
		unitOfMeasure: ShortTextInputSchema.optional(),
		form: ShortTextInputSchema.optional(),
		strength: ShortTextInputSchema.optional(),
		isActive: z.boolean().optional(),
		metadata: z.record(z.string(), z.unknown()).optional(),
	})
	.meta({ id: "CreateProduct" })

export type CreateProduct = z.infer<typeof CreateProductSchema>

export const PortalProductSchema = ProductSchema.extend({
	availability: ProductAvailabilitySchema,
	variants: z.array(ProductVariantSchema).default([]),
	offerings: z.array(CheckoutOfferingSchema).default([]),
}).meta({ id: "PortalProduct" })

export type PortalProduct = z.infer<typeof PortalProductSchema>

export const CreateProductVariantSchema = z
	.object({
		code: RequiredShortTextInputSchema.optional(),
		name: RequiredShortTextInputSchema.optional(),
		description: ProductDescriptionInputSchema.optional(),
		dose: ShortTextInputSchema.optional(),
		isActive: z.boolean().optional(),
	})
	.meta({ id: "CreateProductVariant" })

export type CreateProductVariant = z.infer<typeof CreateProductVariantSchema>

export const UpdateProductVariantSchema = z
	.object({
		code: RequiredShortTextInputSchema.nullable().optional(),
		name: RequiredShortTextInputSchema.nullable().optional(),
		description: ProductDescriptionInputSchema.nullable().optional(),
		dose: ShortTextInputSchema.nullable().optional(),
		isActive: z.boolean().optional(),
	})
	.meta({ id: "UpdateProductVariant" })

export type UpdateProductVariant = z.infer<typeof UpdateProductVariantSchema>

export const CreateProductVariantSkuSchema = z
	.object({
		code: RequiredShortTextInputSchema.optional(),
		name: RequiredShortTextInputSchema.optional(),
		description: ProductDescriptionInputSchema.optional(),
		daysSupply: z.number().int().positive(),
		currency: RequiredShortTextInputSchema.default("USD"),
		unitAmountCents: z.number().int().nonnegative(),
		isActive: z.boolean().optional(),
	})
	.meta({ id: "CreateProductVariantSku" })

export type CreateProductVariantSku = z.infer<typeof CreateProductVariantSkuSchema>

export const UpdateProductVariantSkuSchema = z
	.object({
		code: RequiredShortTextInputSchema.nullable().optional(),
		name: RequiredShortTextInputSchema.nullable().optional(),
		description: ProductDescriptionInputSchema.nullable().optional(),
		daysSupply: z.number().int().positive().optional(),
		currency: RequiredShortTextInputSchema.optional(),
		unitAmountCents: z.number().int().nonnegative().optional(),
		isActive: z.boolean().optional(),
	})
	.meta({ id: "UpdateProductVariantSku" })

export type UpdateProductVariantSku = z.infer<typeof UpdateProductVariantSkuSchema>

export const CreateCheckoutOfferingSchema = withRecurringMembershipInputValidation(
	z
	.object({
		code: RequiredShortTextInputSchema.optional(),
		name: RequiredShortTextInputSchema,
		description: ProductDescriptionInputSchema.optional(),
		daysSupply: z.number().int().positive(),
		currency: RequiredShortTextInputSchema.default("USD"),
		unitAmountCents: z.number().int().nonnegative(),
		isActive: z.boolean().optional(),
		assignments: z.array(JourneyDefinitionOfferingAssignmentInputSchema).default([]),
	})
	.extend(RecurringMembershipFieldsInputSchema.shape)
	.extend({ refillAllowance: z.number().int().nonnegative() }),
)
	.meta({ id: "CreateCheckoutOffering" })

export type CreateCheckoutOffering = z.infer<typeof CreateCheckoutOfferingSchema>

export const UpdateCheckoutOfferingSchema = withRecurringMembershipInputValidation(
	z
	.object({
		code: RequiredShortTextInputSchema.nullable().optional(),
		name: RequiredShortTextInputSchema.optional(),
		description: ProductDescriptionInputSchema.nullable().optional(),
		daysSupply: z.number().int().positive().optional(),
		currency: RequiredShortTextInputSchema.optional(),
		unitAmountCents: z.number().int().nonnegative().optional(),
		isActive: z.boolean().optional(),
		assignments: z.array(JourneyDefinitionOfferingAssignmentInputSchema).optional(),
	})
	.extend(RecurringMembershipFieldsInputSchema.shape),
)
	.meta({ id: "UpdateCheckoutOffering" })

export type UpdateCheckoutOffering = z.infer<typeof UpdateCheckoutOfferingSchema>

export const UpdateProductSchema = z
	.object({
		ndc: RequiredShortTextInputSchema.optional(),
		sku: RequiredShortTextInputSchema.nullable().optional(),
		productCode: RequiredShortTextInputSchema.nullable().optional(),
		csn: RequiredShortTextInputSchema.nullable().optional(),
		name: RequiredShortTextInputSchema.optional(),
		description: ProductDescriptionInputSchema.nullable().optional(),
		dose: ShortTextInputSchema.nullable().optional(),
		daysSupply: z.number().int().positive().nullable().optional(),
		unitOfMeasure: ShortTextInputSchema.nullable().optional(),
		form: ShortTextInputSchema.nullable().optional(),
		strength: ShortTextInputSchema.nullable().optional(),
		isActive: z.boolean().optional(),
		metadata: z.record(z.string(), z.unknown()).optional(),
	})
	.meta({ id: "UpdateProduct" })

export type UpdateProduct = z.infer<typeof UpdateProductSchema>

export const CreateVisitFeeRuleSchema = z
	.object({
		code: RequiredShortTextInputSchema.optional(),
		name: RequiredShortTextInputSchema,
		description: ProductDescriptionInputSchema.optional(),
		visitType: VisitTypeSchema,
		journeyDefinitionId: JourneyDefinitionIdSchema.optional(),
		journeyDefinitionVersion: z.number().int().positive().optional(),
		currency: RequiredShortTextInputSchema.default("USD"),
		unitAmountCents: z.number().int().nonnegative(),
		isActive: z.boolean().optional(),
	})
	.superRefine((rule, context) => {
		if (rule.visitType === "none") {
			context.addIssue({
				code: "custom",
				message: "Visit fee rules require a billable visit type.",
				path: ["visitType"],
			})
		}
		if ((rule.journeyDefinitionId === undefined) !== (rule.journeyDefinitionVersion === undefined)) {
			context.addIssue({
				code: "custom",
				message: "Journey definition ID and version must be provided together.",
				path: ["journeyDefinitionVersion"],
			})
		}
	})
	.meta({ id: "CreateVisitFeeRule" })

export type CreateVisitFeeRule = z.infer<typeof CreateVisitFeeRuleSchema>

export const UpdateVisitFeeRuleSchema = z
	.object({
		code: RequiredShortTextInputSchema.nullable().optional(),
		name: RequiredShortTextInputSchema.optional(),
		description: ProductDescriptionInputSchema.nullable().optional(),
		visitType: VisitTypeSchema.optional(),
		journeyDefinitionId: JourneyDefinitionIdSchema.nullable().optional(),
		journeyDefinitionVersion: z.number().int().positive().nullable().optional(),
		currency: RequiredShortTextInputSchema.optional(),
		unitAmountCents: z.number().int().nonnegative().optional(),
		isActive: z.boolean().optional(),
	})
	.superRefine((rule, context) => {
		if (rule.visitType === "none") {
			context.addIssue({
				code: "custom",
				message: "Visit fee rules require a billable visit type.",
				path: ["visitType"],
			})
		}
		const hasDefinitionId = rule.journeyDefinitionId !== undefined
		const hasDefinitionVersion = rule.journeyDefinitionVersion !== undefined
		if (hasDefinitionId !== hasDefinitionVersion) {
			context.addIssue({
				code: "custom",
				message: "Journey definition ID and version must be updated together.",
				path: ["journeyDefinitionVersion"],
			})
			return
		}
		if (
			hasDefinitionId &&
			(rule.journeyDefinitionId === null) !== (rule.journeyDefinitionVersion === null)
		) {
			context.addIssue({
				code: "custom",
				message: "Journey definition ID and version must both be set or both be null.",
				path: ["journeyDefinitionVersion"],
			})
		}
	})
	.meta({ id: "UpdateVisitFeeRule" })

export type UpdateVisitFeeRule = z.infer<typeof UpdateVisitFeeRuleSchema>

export const ProductFiltersSchema = z.object({
	search: z.string().trim().apply(utf16Length("max", 200)).optional(),
	isActive: z.enum(["true", "false"]).optional(),
	page: z.coerce.number().int().positive().default(1),
	limit: z.coerce.number().int().positive().max(100).default(25),
}).meta({ id: "ProductFilters" })

export type ProductFilters = z.infer<typeof ProductFiltersSchema>
